import { FileType } from '../../models/document';
import { downloadFileFromBlob } from '../../utils/download-blob';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  AggregatedHoldingsReportModel,
  ICreateReportRequestObject,
  IDownloadReportRequestObject, IInvestmentReportDisplaySections,
  IReportRequestDataResult,
  IRunReportRequestObject,
  IRunReportResponseObject,
  ReportDocumentType
} from "../../models/reports";
import { BaseHttpService } from './base.http.service';
import { base64ToArrayBuffer } from 'src/app/utils/base64-converter';
import { PeriodRangeType } from 'src/app/models/date';
import { AppConfig } from 'src/app/app.config';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { parseError } from '../../utils/error-parse';
import { SnackBarService } from '../snackbar/snackBar.service';
import { DatePipe } from '@angular/common';
import { IEnvironmentConfig } from 'src/app/models/environmentConfig';

@Injectable()
export class ReportsService extends BaseHttpService {
  fileName: string;

  private _reportLoading$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public reportLoading$: Observable<any> = this._reportLoading$.asObservable();

  constructor(
    private http: HttpClient,
    private appConfig: AppConfig,
    private snackBarService: SnackBarService,
    private datePipe: DatePipe,
  ) {
    super();
  }

  get envSettings(): IEnvironmentConfig {
    return this.appConfig.getEnvironmentConfig();
  }

  /**
   * Sends request to correct api depending on report type. 
   * 
   * Triggers download if report type needs to be downloaded. This is 
   * determined by the reponse type, if its blob we download the file.  
   * 
   * Opens a snackbar with a success or error notfication depending on response.
   * 
   * @param runReportRequestObject the request to generate report. 
   */
  public generateReport(runReportRequestObject: IRunReportRequestObject): void {
    this._reportLoading$.next(true);

    this.runReport(runReportRequestObject)
      .pipe(
        switchMap((response: HttpResponse<Object | Blob>) => {
          if (response.body instanceof Blob) {
            return this.downloadFile(response as HttpResponse<Blob>).pipe(
              map(
                () =>
                  'Success! The report has been generated and downloaded.',
              ),
            );
          } else {
            return of('The report is currently running and will be in View Reports when available.');
          }
        }),
        tap((message: string) => {
          this._reportLoading$.next(false);
          this.snackBarService.showSnackBar(message);
        }),
        catchError((err) => {
          this._reportLoading$.next(false);
          this.snackBarService.showWarningSnackBar("An error occured while generating your report.");
          return of();
        }),
      )
      .subscribe();
  }

  public downloadReport(
    downloadReportRequestObject: IDownloadReportRequestObject,
  ) {
    let url: string;
    url = `${this.envSettings.webApiUrl}documents/${downloadReportRequestObject.documentId}`;

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob',
      headers: this.getHttpHeaders(),
    });
  }

  public retrieveDocumentList(entityId: number) {
    const url = `${this.envSettings.webApiUrl}documents/investor/${entityId}`;

    return this.http.get(url, {
      observe: 'body',
      responseType: 'json',
      headers: this.getHttpHeaders(),
    });
  }

  public retrieveDocument(documentObject: any) {
    this.fileName = documentObject.fileName;
    const token = encodeURIComponent(documentObject.token);
    const url = `${this.envSettings.webApiUrl}documents/?token=${token}`;

    return this.http.get(url, {
      observe: 'body',
      responseType: 'blob',
      headers: this.getHttpHeaders(),
    });
  }

  public writePdfFromBlob(reportBlob: any) {
    const observable = new Observable((observer) => {
      const blob = new Blob([reportBlob], { type: 'application/pdf' });
      downloadFileFromBlob(blob, this.fileName);
      observer.next();
    });
    return observable;
  }

  public writeFileFromBlob(
    stream: Blob,
    fileType: FileType,
    name: string = '',
  ) {
    const observable = new Observable((observer) => {
      const blob = new Blob([stream], { type: fileType });
      downloadFileFromBlob(blob, name);
      observer.next();
    });
    return observable;
  }

  public writePdfFromDocument(reportDocument: IRunReportResponseObject) {
    const fileName = reportDocument.fileName;
    const reportBlob = base64ToArrayBuffer(reportDocument.fileStream);
    const observable = new Observable((observer) => {
      const blob = new Blob([reportBlob], { type: 'application/pdf' });
      downloadFileFromBlob(blob, fileName);
      observer.next();
    });
    return observable;
  }

  public getReportRequestData(): Observable<IReportRequestDataResult> {
    const url = `${this.envSettings.webApiUrl}reportrequest`;

    return this.http
      .get(url, {
        observe: "body",
        responseType: "json",
        headers: this.getHttpHeaders()
      })
      .pipe(
        map((res) => {
          const data = { reportRequestData: res } as IReportRequestDataResult;
          return data;
        }),
      );
  }

  public getAggregatedHoldingsReport(
    queryModel: AggregatedHoldingsReportModel,
  ): Observable<any> {
    const url = `${this.envSettings.webApiUrl}admin/reports/holdings`;
    const jsonBody = JSON.stringify(queryModel);

    return this.http.post(url, jsonBody, {
      responseType: 'json',
      headers: this.getHttpHeaders(),
    });
  }

  /**
   * Based on the report type determines which endpoint to send the request to.
   * 
   * @param runReportRequestObject the request to generate report.
   * @returns the response. 
   */
  private runReport(
    runReportRequestObject: IRunReportRequestObject,
  ): Observable<HttpResponse<Blob | Object>> {
    switch (runReportRequestObject.documentType) {
      case ReportDocumentType.InvestorListReport:
        return this.getInvestorListReport(runReportRequestObject);
      case ReportDocumentType.AllHoldingsReport:
      case ReportDocumentType.CapitalMovementsReport:
      case ReportDocumentType.PortfolioPerformanceReport:
      case ReportDocumentType.MemberListReport:
      case ReportDocumentType.TaxSummaryReport:
      case ReportDocumentType.ApsReport:
        return this.generateReportRequest(runReportRequestObject);
      case ReportDocumentType.GeneralLedgerReport:
      case ReportDocumentType.InvestmentReportByClassification:
      case ReportDocumentType.ManagementAccountsReport:
        return this.getReport(runReportRequestObject);
    }
  }

  /**
   * Sends a request to the endpoint that generates and returns the report. 
   * Uses the document type and the end date to the generated url.
   * 
   * @param runReportRequestObject the request to generate report.
   * @returns the file repsonse.
   */
  private getInvestorListReport(
    runReportRequestObject: IRunReportRequestObject,
  ): Observable<HttpResponse<Blob>> {
    const url: string =
      this.envSettings.webApiUrl +
      'documents/investors/report/' +
      runReportRequestObject.documentType +
      '?reportDate=' +
      this.datePipe.transform(runReportRequestObject.period.endDate, 'yyyy-MM-dd');
    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob',
      headers: this.getHttpHeaders(),
    });
  }

  /**
   * Sends a request to the endpoint to create the request to generate the 
   * report. Uses the documentType, period, allHoldingsReportType and 
   * portfolioIds when applicable to create the request.
   * 
   * @param runReportRequestObject the request to generate report.
   * @returns the response if the request has been created.
   */
  private generateReportRequest(
    runReportRequestObject: IRunReportRequestObject,
  ): Observable<HttpResponse<Object>> {
    const url: string = this.envSettings.webApiUrl + 'reportrequest/create';

    const createReportRequestModel: ICreateReportRequestObject = {
      reportType: runReportRequestObject.documentType,
      parameters: {
        startDate: this.datePipe.transform(runReportRequestObject.period?.startDate, 'yyyy-MM-dd'),
        endDate: this.datePipe.transform(runReportRequestObject.period?.endDate, 'yyyy-MM-dd'),
        periodType: runReportRequestObject.period?.periodType,
        allHoldingsReportType: runReportRequestObject.allHoldingsReportType,
        portfolioIds: runReportRequestObject.portfolio ? [runReportRequestObject.portfolio.id] : null,
      },
    };

    return this.http.post(url, createReportRequestModel, {
      observe: 'response',
      responseType: 'json',
      headers: this.getHttpHeaders(),
    });
  }

  /**
   * Sends a request to the endpoint that generates and returns the report. 
   * Uses the document type, portfolio, period type and end date to the 
   * generated url. 
   * 
   * We create a request body from the sections (these are only needed for 
   * investment report) and set download to true for all so we get back file 
   * response.
   * 
   * If the period type is custom we use the start date. 
   * 
   * @param runReportRequestObject the request to generate report.
   * @returns the file repsonse.
   */
  private getReport(
    runReportRequestObject: IRunReportRequestObject,
  ): Observable<HttpResponse<Blob>> {
    let url: string =
      this.envSettings.webApiUrl +
      'documents/investor/' +
      runReportRequestObject.portfolio.entityId +
      '/portfolio/' +
      runReportRequestObject.portfolio.id +
      '/report/' +
      runReportRequestObject.documentType +
      '?periodType=' +
      runReportRequestObject.period.periodType +
      '&reportDate=' +
      this.datePipe.transform(runReportRequestObject.period.endDate, 'yyyy-MM-dd');

    if (runReportRequestObject.period.periodType === PeriodRangeType.custom) {
      url += '&startDate=' + this.datePipe.transform(runReportRequestObject.period.startDate, 'yyyy-MM-dd');
    }

    const displayReportSectionsObject: IInvestmentReportDisplaySections = {
      DisplayValuationSection: runReportRequestObject.sections?.valuation,
      DisplayPortfolioSummarySection: runReportRequestObject.sections?.portfoliosummary,
      DisplayStatementSummarySection: runReportRequestObject.sections?.statementsummary,
      DisplayTransactionSection: runReportRequestObject.sections?.transaction,
      DisplayPerformanceSection: runReportRequestObject.sections?.performance,
      DownloadOnly: true,
    };

    return this.http.post(url, displayReportSectionsObject, {
      observe: 'response',
      responseType: 'blob',
      headers: this.getHttpHeaders(),
    });
  }

  /**
   * Downloads the file. Generates a file name and a file type based on reponse
   * headers.
   * 
   * Unsure on why writeFileFromBlob returns an empty observable, could be 
   * refactored to be a little tidier.
   * 
   * @param response the file reponse.
   * @returns empty observable
   */
  private downloadFile(response: HttpResponse<Blob>): Observable<unknown> {
    const fileName: string = response.headers
      .get('content-disposition')
      .split('filename=')[1]
      .replace(/['"]+/g, '');
    const fileType: FileType = Object.entries(FileType).find(
      (file: [string, string]) =>
        file[1] === response.headers.get('content-type'),
    )[1];
    return this.writeFileFromBlob(response.body, fileType, fileName);
  }
}
